import React, { useEffect, useMemo, useState } from "react";
import "./backendLab.css";

const FAKE_LATENCY = 2000;

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const prettyJson = (obj) => JSON.stringify(obj, null, 2);

const Modal = ({ open, title, subtitle, icon, onClose, children }) => {
  // ESC close + body lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="blModalOverlay" onClick={(e) => e.target.classList.contains("blModalOverlay") && onClose()}>
      <div className="blModal" role="dialog" aria-modal="true">
        <button className="blModalClose" type="button" onClick={onClose} aria-label="Close">
          <i className="uil uil-times"></i>
        </button>

        <div className="blModalHeader">
          <div className="blModalBadge">
            <i className={icon}></i>
          </div>
          <div>
            <h3 className="blModalTitle">{title}</h3>
            <p className="blModalSubtitle">{subtitle}</p>
          </div>
        </div>

        <div className="blModalBody">{children}</div>

        <div className="blModalFooter">
          <span className="blHint">
            Tip: this is for you to test real backend flows — patterns match production.
          </span>
          <button className="blBtn blBtnGhost" type="button" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

/* =========================
   SIMULATIONS
========================= */

const ApiCallsSim = () => {
  const apis = useMemo(
    () => [
      {
        key: "weather",
        name: "Weather API",
        route: "/api/v1/weather",
        params: { city: "Abuja", units: "metric", lang: "en" },
        explain: "Test a server fetching weather data from a provider, caching it, then returning normalized results.",
      },
      {
        key: "forex",
        name: "Forex API",
        route: "/api/v1/forex",
        params: { base: "USD", quote: "NGN", source: "aggregated" },
        explain: "Test exchange-rate retrieval with provider failover and consistent rounding.",
      },
      {
        key: "traffic",
        name: "Traffic API",
        route: "/api/v1/traffic",
        params: { city: "Lagos", radius_km: 10, mode: "driving" },
        explain: "Test traffic density scoring (ETA bands) using queued background refresh jobs.",
      },
      {
        key: "status",
        name: "System Health API",
        route: "/api/v1/status",
        params: { region: "eu-west-1", verbose: true },
        explain: "Test health checks from multiple services (db, cache, queue) and returns a consolidated status.",
      },
    ],
    []
  );

  const [selected, setSelected] = useState(apis[0]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = () => {
    setLoading(true);
    setResult(null);

    setTimeout(() => {
      if (selected.key === "weather") {
        const temp = rand(26, 36);
        const humidity = rand(35, 75);
        setResult({
          ok: true,
          cached: Math.random() > 0.5,
          data: {
            city: selected.params.city,
            units: selected.params.units,
            condition: ["Sunny", "Cloudy", "Light Rain", "Haze"][rand(0, 3)],
            temperature: temp,
            feels_like: temp + rand(-1, 2),
            humidity,
            wind_kph: rand(4, 18),
          },
          meta: { request_id: `req_${rand(100000, 999999)}`, latency_ms: rand(120, 340) },
        });
      }

      if (selected.key === "forex") {
        const rate = clamp(1500 + rand(-80, 80), 1300, 1700);
        setResult({
          ok: true,
          data: {
            pair: `${selected.params.base}/${selected.params.quote}`,
            rate,
            inverse: 1 / rate,
            provider: ["provider-a", "provider-b", "provider-c"][rand(0, 2)],
            as_of: new Date().toISOString(),
          },
          meta: { request_id: `req_${rand(100000, 999999)}`, latency_ms: rand(90, 220) },
        });
      }

      if (selected.key === "traffic") {
        setResult({
          ok: true,
          data: {
            city: selected.params.city,
            mode: selected.params.mode,
            radius_km: selected.params.radius_km,
            congestion_score: rand(18, 92),
            eta_to_center_min: rand(12, 55),
            advisory: ["Smooth roads", "Moderate congestion", "Heavy congestion—avoid peak hours"][rand(0, 2)],
          },
          meta: { request_id: `req_${rand(100000, 999999)}`, latency_ms: rand(140, 420) },
        });
      }

      if (selected.key === "status") {
        setResult({
          ok: true,
          data: {
            region: selected.params.region,
            services: {
              database: Math.random() > 0.05 ? "ok" : "degraded",
              cache: Math.random() > 0.08 ? "ok" : "degraded",
              queue: Math.random() > 0.06 ? "ok" : "degraded",
              api: "ok",
            },
            uptime_days: rand(12, 190),
          },
          meta: { request_id: `req_${rand(100000, 999999)}`, latency_ms: rand(60, 160) },
        });
      }

      setLoading(false);
    }, FAKE_LATENCY);
  };

  return (
    <div className="blGridTwo">
      <div className="blPanel">
        <h4 className="blH4">Choose an API</h4>
        <p className="blP">
          Real backends validate inputs, authenticate requests, fetch/compute, and return a stable contract (JSON).
        </p>

        <div className="blList">
          {apis.map((a) => (
            <button
              key={a.key}
              type="button"
              className={selected.key === a.key ? "blListItem blListItemActive" : "blListItem"}
              onClick={() => {
                setSelected(a);
                setResult(null);
              }}
            >
              <span className="blListTitle">{a.name}</span>
              <span className="blListSub">{a.route}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="blPanel">
        <div className="blReqHead">
          <div>
            <h4 className="blH4">Request Preview</h4>
            <p className="blP">{selected.explain}</p>
          </div>
          <span className="blChip">GET</span>
        </div>

        <div className="blCodeWrap">
          <div className="blCodeTop">
            <span className="blMono">Endpoint</span>
            <span className="blMono">{selected.route}</span>
          </div>
          <pre className="blCode">{prettyJson({ params: selected.params })}</pre>
        </div>

        <div className="blActions">
          <button className="blBtn blBtnPrimary" type="button" onClick={run} disabled={loading}>
            {loading ? (
              <>
                <span className="blSpinner" aria-hidden="true" /> Testing…
              </>
            ) : (
              <>
                Run Test <i className="uil uil-play"></i>
              </>
            )}
          </button>

          <div className={loading ? "blProgress blProgressRun" : "blProgress"}>
            <span />
          </div>
        </div>

        {result && (
          <div className="blResult">
            <div className="blResultHead">
              <span className="blOk">
                <i className="uil uil-check-circle"></i> 200 OK
              </span>
              <span className="blMeta">
                {result.meta.request_id} • {result.meta.latency_ms}ms
              </span>
            </div>
            <pre className="blCode blCodeResult">{prettyJson(result)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

const ValidationSim = () => {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({ email: "", password: "", age: "" });
  const [errors, setErrors] = useState({});
  const [ok, setOk] = useState(null);

  const validate = () => {
    const e = {};
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email);
    if (!emailOk) e.email = "Email must be valid (e.g. name@example.com).";
    if (values.password.length < 8) e.password = "Password must be at least 8 characters.";
    const age = parseInt(values.age || "0", 10);
    if (!Number.isFinite(age) || age < 16) e.age = "Age must be 16+ (demo rule).";
    return e;
  };

  const submit = (e) => {
    e.preventDefault();
    setOk(null);

    const eMap = validate();
    setErrors(eMap);

    if (Object.keys(eMap).length) return;

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setOk({
        ok: true,
        message: "Validation passed. Payload accepted.",
        normalized: {
          email: values.email.toLowerCase(),
          password_hash: "bcrypt:$2b$10$•••",
          age: parseInt(values.age, 10),
        },
      });
    }, FAKE_LATENCY);
  };

  return (
    <div className="blGridTwo">
      <div className="blPanel">
        <h4 className="blH4">Server-side validation</h4>
        <p className="blP">
          Real backends validate again even if frontend validates. This prevents tampering and protects data integrity.
        </p>

        <div className="blBullets">
          <div className="blBullet">
            <i className="uil uil-shield-check"></i>
            <span>Schema validation (shape, types, constraints)</span>
          </div>
          <div className="blBullet">
            <i className="uil uil-lock-access"></i>
            <span>Security rules (password policy, rate limits)</span>
          </div>
          <div className="blBullet">
            <i className="uil uil-analytics"></i>
            <span>Normalization (lowercasing, trimming, hashing)</span>
          </div>
        </div>
      </div>

      <div className="blPanel">
        <h4 className="blH4">Test validation flow</h4>

        <form className="blForm" onSubmit={submit}>
          <label className="blLabel">
            Email
            <input
              className={errors.email ? "blInput blInputErr" : "blInput"}
              value={values.email}
              onChange={(e) => setValues((s) => ({ ...s, email: e.target.value }))}
              placeholder="name@example.com"
            />
            {errors.email && <span className="blErr">{errors.email}</span>}
          </label>

          <label className="blLabel">
            Password
            <input
              type="password"
              className={errors.password ? "blInput blInputErr" : "blInput"}
              value={values.password}
              onChange={(e) => setValues((s) => ({ ...s, password: e.target.value }))}
              placeholder="min 8 characters"
            />
            {errors.password && <span className="blErr">{errors.password}</span>}
          </label>

          <label className="blLabel">
            Age
            <input
              inputMode="numeric"
              className={errors.age ? "blInput blInputErr" : "blInput"}
              value={values.age}
              onChange={(e) => setValues((s) => ({ ...s, age: e.target.value }))}
              placeholder="16+"
            />
            {errors.age && <span className="blErr">{errors.age}</span>}
          </label>

          <div className="blActions">
            <button className="blBtn blBtnPrimary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="blSpinner" aria-hidden="true" /> Validating…
                </>
              ) : (
                <>
                  Submit <i className="uil uil-check"></i>
                </>
              )}
            </button>

            <button
              className="blBtn blBtnGhost"
              type="button"
              onClick={() => {
                setValues({ email: "", password: "", age: "" });
                setErrors({});
                setOk(null);
              }}
            >
              Reset
            </button>

            <div className={loading ? "blProgress blProgressRun" : "blProgress"}>
              <span />
            </div>
          </div>
        </form>

        {ok && (
          <div className="blResult">
            <div className="blResultHead">
              <span className="blOk">
                <i className="uil uil-check-circle"></i> Accepted
              </span>
              <span className="blMeta">normalized payload</span>
            </div>
            <pre className="blCode blCodeResult">{prettyJson(ok)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

const AuthSim = () => {
  const [mode, setMode] = useState("login"); // login | signup
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({ email: "", password: "", confirm: "" });
  const [msg, setMsg] = useState(null);

  const run = () => {
    setMsg(null);

    if (!values.email || !values.password) {
      setMsg({ type: "err", text: "Email and password are required." });
      return;
    }
    if (mode === "signup" && values.password !== values.confirm) {
      setMsg({ type: "err", text: "Passwords do not match." });
      return;
    }

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const fakeToken = `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.${rand(100000, 999999)}.${rand(100000, 999999)}`;
      setMsg({
        type: "ok",
        text:
          mode === "login"
            ? "Login successful. Session created."
            : "Signup successful. Account created and session started.",
        payload: {
          ok: true,
          user: { id: `usr_${rand(1000, 9999)}`, email: values.email.toLowerCase() },
          token: fakeToken,
          expires_in: "15m",
          refresh_token: `rft_${rand(100000, 999999)}`,
        },
      });
    }, FAKE_LATENCY);
  };

  return (
    <div className="blGridTwo">
      <div className="blPanel">
        <h4 className="blH4">Authentication & sessions</h4>
        <p className="blP">
          Real backends issue tokens, manage sessions, hash passwords, enforce rate limits, and protect endpoints.
        </p>

        <div className="blTabs">
          <button
            type="button"
            className={mode === "login" ? "blTab blTabActive" : "blTab"}
            onClick={() => {
              setMode("login");
              setMsg(null);
            }}
          >
            Login
          </button>
          <button
            type="button"
            className={mode === "signup" ? "blTab blTabActive" : "blTab"}
            onClick={() => {
              setMode("signup");
              setMsg(null);
            }}
          >
            Signup
          </button>
        </div>

        <div className="blBullets">
          <div className="blBullet">
            <i className="uil uil-lock"></i>
            <span>Password hashing (bcrypt/argon2)</span>
          </div>
          <div className="blBullet">
            <i className="uil uil-shield"></i>
            <span>JWT / session cookies</span>
          </div>
          <div className="blBullet">
            <i className="uil uil-bolt"></i>
            <span>Rate limiting & brute-force protection</span>
          </div>
        </div>
      </div>

      <div className="blPanel">
        <h4 className="blH4">{mode === "login" ? "Test Login" : "Test Signup"}</h4>

        <div className="blForm">
          <label className="blLabel">
            Email
            <input
              className="blInput"
              value={values.email}
              onChange={(e) => setValues((s) => ({ ...s, email: e.target.value }))}
              placeholder="name@example.com"
            />
          </label>

          <label className="blLabel">
            Password
            <input
              type="password"
              className="blInput"
              value={values.password}
              onChange={(e) => setValues((s) => ({ ...s, password: e.target.value }))}
              placeholder="••••••••"
            />
          </label>

          {mode === "signup" && (
            <label className="blLabel">
              Confirm Password
              <input
                type="password"
                className="blInput"
                value={values.confirm}
                onChange={(e) => setValues((s) => ({ ...s, confirm: e.target.value }))}
                placeholder="••••••••"
              />
            </label>
          )}

          <div className="blActions">
            <button className="blBtn blBtnPrimary" type="button" onClick={run} disabled={loading}>
              {loading ? (
                <>
                  <span className="blSpinner" aria-hidden="true" /> Processing…
                </>
              ) : (
                <>
                  Run <i className="uil uil-play"></i>
                </>
              )}
            </button>

            <div className={loading ? "blProgress blProgressRun" : "blProgress"}>
              <span />
            </div>
          </div>

          {msg && (
            <div className={msg.type === "ok" ? "blToast blToastOk" : "blToast blToastErr"}>
              <div className="blToastHead">
                <i className={msg.type === "ok" ? "uil uil-check-circle" : "uil uil-exclamation-triangle"}></i>
                <span>{msg.text}</span>
              </div>

              {msg.payload && <pre className="blCode blCodeResult">{prettyJson(msg.payload)}</pre>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CrudSim = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([
    { id: "tsk_1001", title: "Design API contract", status: "open" },
    { id: "tsk_1002", title: "Implement validation schema", status: "open" },
    { id: "tsk_1003", title: "Add auth middleware", status: "done" },
  ]);

  const [draft, setDraft] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const fake = (fn) => {
    setLoading(true);
    setTimeout(() => {
      fn();
      setLoading(false);
    }, FAKE_LATENCY);
  };

  const create = () => {
    if (!draft.trim()) return;
    const text = draft.trim();
    fake(() => {
      setItems((s) => [{ id: `tsk_${rand(2000, 9999)}`, title: text, status: "open" }, ...s]);
      setDraft("");
    });
  };

  const toggle = (id) => {
    fake(() => {
      setItems((s) =>
        s.map((it) => (it.id === id ? { ...it, status: it.status === "done" ? "open" : "done" } : it))
      );
    });
  };

  const startEdit = (id, title) => {
    setEditId(id);
    setEditText(title);
  };

  const saveEdit = () => {
    if (!editText.trim()) return;
    fake(() => {
      setItems((s) => s.map((it) => (it.id === editId ? { ...it, title: editText.trim() } : it)));
      setEditId(null);
      setEditText("");
    });
  };

  const remove = (id) => {
    fake(() => setItems((s) => s.filter((it) => it.id !== id)));
  };

  return (
    <div className="blGridTwo">
      <div className="blPanel">
        <h4 className="blH4">CRUD operations</h4>
        <p className="blP">
          Create, Read, Update and Delete. Backends also enforce auth, validation,
          and transactional integrity.
        </p>

        <div className="blBullets">
          <div className="blBullet">
            <i className="uil uil-database"></i>
            <span>Database writes + constraints</span>
          </div>
          <div className="blBullet">
            <i className="uil uil-sync"></i>
            <span>Optimistic UI / conflict handling</span>
          </div>
          <div className="blBullet">
            <i className="uil uil-clipboard-notes"></i>
            <span>Audit logs & observability</span>
          </div>
        </div>

        <div className="blActions">
          <div className={loading ? "blProgress blProgressRun" : "blProgress"}>
            <span />
          </div>
          <span className="blChip">{loading ? "DB BUSY" : "DB READY"}</span>
        </div>
      </div>

      <div className="blPanel">
        <h4 className="blH4">Task API</h4>

        <div className="blRow">
          <input
            className="blInput"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Create a task…"
          />
          <button className="blBtn blBtnPrimary" type="button" onClick={create} disabled={loading}>
            Add <i className="uil uil-plus"></i>
          </button>
        </div>

        <div className="blCrudList">
          {items.map((it) => (
            <div key={it.id} className={it.status === "done" ? "blCrudItem blDone" : "blCrudItem"}>
              <button className="blCrudToggle" type="button" onClick={() => toggle(it.id)} disabled={loading}>
                <i className={it.status === "done" ? "uil uil-check-circle" : "uil uil-circle"}></i>
              </button>

              <div className="blCrudMain">
                <div className="blCrudTitle">{it.title}</div>
                <div className="blCrudMeta">{it.id} • {it.status.toUpperCase()}</div>
              </div>

              <div className="blCrudActions">
                <button
                  className="blIconBtn"
                  type="button"
                  onClick={() => startEdit(it.id, it.title)}
                  disabled={loading}
                  aria-label="Edit"
                >
                  <i className="uil uil-pen"></i>
                </button>
                <button
                  className="blIconBtn"
                  type="button"
                  onClick={() => remove(it.id)}
                  disabled={loading}
                  aria-label="Delete"
                >
                  <i className="uil uil-trash-alt"></i>
                </button>
              </div>
            </div>
          ))}
        </div>

        {editId && (
          <div className="blEdit">
            <div className="blEditHead">
              <strong>Edit Task</strong>
              <button className="blIconBtn" type="button" onClick={() => setEditId(null)} aria-label="Close edit">
                <i className="uil uil-times"></i>
              </button>
            </div>
            <input className="blInput" value={editText} onChange={(e) => setEditText(e.target.value)} />
            <div className="blActions">
              <button className="blBtn blBtnPrimary" type="button" onClick={saveEdit} disabled={loading}>
                Save <i className="uil uil-check"></i>
              </button>
              <button className="blBtn blBtnGhost" type="button" onClick={() => setEditId(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* =========================
   SECTION
========================= */

const processes = [
  {
    id: "api",
    label: "API Calls",
    icon: "uil uil-cloud-data-connection",
    title: "API Calls & Data Fetching",
    subtitle: "Test endpoints, parameters, latency, caching and JSON contracts.",
    component: ApiCallsSim,
  },
  {
    id: "validation",
    label: "Validation",
    icon: "uil uil-shield-check",
    title: "Validation & Sanitization",
    subtitle: "Test schema rules, server acceptance, and normalized payloads.",
    component: ValidationSim,
  },
  {
    id: "auth",
    label: "Login / Signup",
    icon: "uil uil-lock-access",
    title: "Authentication",
    subtitle: "Test sessions, tokens, hashing, and secure login/signup flows.",
    component: AuthSim,
  },
  {
    id: "crud",
    label: "CRUD",
    icon: "uil uil-database",
    title: "CRUD Operations",
    subtitle: "Test create/read/update/delete with server-like delays and state transitions.",
    component: CrudSim,
  },
];

const BackendLab = () => {
  const [active, setActive] = useState(null);

  const activeProcess = processes.find((p) => p.id === active);
  const ActiveComp = activeProcess?.component;

  return (
    <section className="backendLab section" id="backend-lab">
      <div className="backendLab__wrapper container">
        <h2 className="section__title">TEST MY BACKEND SKILLS</h2>
        <span className="section__subtitle">I'm really not joking around</span>

        <div className="blIntro">
          <div className="blIntroCard">
            <div className="blIntroIcon">
              <i className="uil uil-server-network"></i>
            </div>
            <div>
              <h3 className="blIntroTitle">Backend patterns — by yours truly</h3>
              <p className="blIntroText">
                Any data inputted is immediately discarded, these show real backend behavior: latency, contracts, validation,
                auth flows, and CRUD lifecycles.
              </p>
            </div>
          </div>

          <div className="blBadges">
            <span className="blBadge"><i className="uil uil-brackets-curly"></i> JSON contracts</span>
            <span className="blBadge"><i className="uil uil-shield"></i> Secure flows</span>
            <span className="blBadge"><i className="uil uil-processor"></i> State machines</span>
          </div>
        </div>

        <div className="blButtons">
          {processes.map((p) => (
            <button key={p.id} className="blProcessBtn" type="button" onClick={() => setActive(p.id)}>
              <span className="blProcessIcon"><i className={p.icon}></i></span>
              <span className="blProcessText">
                <span className="blProcessLabel">{p.label}</span>
                <span className="blProcessSub">Tap to Test</span>
              </span>
              <span className="blProcessArrow"><i className="uil uil-angle-right-b"></i></span>
            </button>
          ))}
        </div>
      </div>

      <Modal
        open={!!activeProcess}
        title={activeProcess?.title}
        subtitle={activeProcess?.subtitle}
        icon={activeProcess?.icon}
        onClose={() => setActive(null)}
      >
        {ActiveComp ? <ActiveComp /> : null}
      </Modal>
    </section>
  );
};

export default BackendLab;
